// Stub → cook adapter.
//
// One entry point: `cookStub(stub, opts)`. The flow:
//
//   1. GRADUATION CHECK — query community-published recipes for an
//      existing recipe whose stubId matches. If found, route the
//      user to that recipe (they cook from the existing community
//      version instead of paying for a fresh AI generation).
//   2. AI GENERATION FALLBACK — no published version exists. Fire
//      generate-recipe with the stub's title / blurb / build-hint /
//      key-ingredients as seed context. Save to user_recipes with
//      `stubId` metadata so future graduation lookups find it.
//   3. RETURN the recipe so the caller can openCookMode().
//
// The graduation logic means every stub-cook adds value to the
// community catalog: the first user to cook a stub becomes its
// canonical version (if they later community-publish it), and
// every subsequent user routes there for free.

import { supabase } from "./supabase";

// Look up an already-built community recipe for this stub.
// Returns the user_recipes row (with .recipe nested) or null.
export async function findPublishedRecipeForStub(stubId) {
  if (!stubId) return null;
  const { data, error } = await supabase
    .from("user_recipes")
    .select("id, slug, user_id, recipe, community_published_at")
    .eq("community_published", true)
    .filter("recipe->>stubId", "eq", stubId)
    .order("community_published_at", { ascending: false })
    .limit(1);
  if (error) {
    console.warn("[cookStub] findPublishedRecipeForStub failed:", error.message);
    return null;
  }
  return stripPrivateNotes(data?.[0] || null);
}

// Community recipes default to public; the author's PERSONAL NOTES are
// private and must never ride along on a community read. Strip them
// from any published-recipe row before it leaves this module.
export function stripPrivateNotes(row) {
  if (!row || !row.recipe || typeof row.recipe !== "object") return row;
  if (!("personalNotes" in row.recipe)) return row;
  const { personalNotes, ...rest } = row.recipe;
  return { ...row, recipe: rest };
}

// Build a full recipe from a stub + save it to user_recipes.
//
// opts:
//   pantry         — array of pantry rows (for canonical binding in
//                    generate-recipe). Optional.
//   saveRecipe     — function(recipe, source) returning the saved
//                    user_recipes row. Pass useUserRecipes(userId)
//                    .saveRecipe. REQUIRED — no save without it.
//   onBuildStart   — fires once the LLM call kicks off (UI hint)
//   onFound        — fires when a community version is found (skips
//                    the build, returns existing recipe)
//
// Returns: { recipe, source }
//   recipe — the recipe object (community OR newly-saved)
//   source — "community" | "ai"
export async function cookStub(stub, opts = {}) {
  if (!stub?.id) throw new Error("cookStub requires a stub with an id");

  // 1. Graduation check.
  const published = await findPublishedRecipeForStub(stub.id);
  if (published?.recipe) {
    opts.onFound?.(published.recipe);
    return { recipe: published.recipe, source: "community", row: published };
  }

  // 2. AI generation.
  if (typeof opts.saveRecipe !== "function") {
    throw new Error("cookStub requires opts.saveRecipe (useUserRecipes.saveRecipe)");
  }
  opts.onBuildStart?.();

  const { data, error } = await supabase.functions.invoke("generate-recipe", {
    body: {
      mode: "final",
      prefs: {
        mealPrompt:       stub.title,
        sketchTitle:      stub.title,
        sketchSubtitle:   stub.blurb || "",
        sketchRationale:  stub.buildHint || "",
        cuisine:          stub.cuisine || "other",
        course:           stub.course || "main",
        mealTiming:       stub.mealTiming || null,
        difficulty:       stub.difficulty || "easy",
        dishContract: {
          tier: "SPECIFIC",
          dishName: stub.title,
          requiredIngredients: Array.isArray(stub.keyIngredients) ? stub.keyIngredients : [],
        },
      },
      pantry: Array.isArray(opts.pantry) ? opts.pantry : [],
    },
  });
  if (error) throw error;
  const generated = data?.recipe;
  if (!generated) throw new Error("generate-recipe returned no recipe");

  // 3. Save with stubId metadata. The JSON column on user_recipes
  // is flexible — no migration needed to add a custom field.
  const withStubId = { ...generated, stubId: stub.id };
  const savedRow = await opts.saveRecipe(withStubId, "ai");
  return {
    recipe: savedRow?.recipe || withStubId,
    source: "ai",
    row: savedRow,
  };
}
